import React, { useEffect, useMemo, useState } from 'react';
import PayPalHostedButton from './PayPalHostedButton';
import { calculateOrder, formatCad, getOrderSummaryLabel, type ShipCountry } from '../../utils/shopPricing';
import './ShopPage.css';

const SHOP_GALLERY_IMAGES = [
  { src: '/shop-board-set.png', alt: 'Migoyugo board set with pieces on a table' },
  { src: '/shop-piece-bags.png', alt: 'Black and white Migoyugo pieces with storage bags' },
  { src: '/shop-pieces-close.png', alt: 'Close-up of Migoyugo playing pieces' },
  { src: '/shop-carry-bag.png', alt: 'Blue carrying bag for the Migoyugo set' },
] as const;

interface ShopPageProps {
  onBack: () => void;
}

const ShopPage: React.FC<ShopPageProps> = ({ onBack }) => {
  const [sets, setSets] = useState<1 | 2>(1);
  const [country, setCountry] = useState<ShipCountry>('CA');
  const [copied, setCopied] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    document.body.classList.add('shop-page-open');
    document.documentElement.classList.add('shop-page-open');
    return () => {
      document.body.classList.remove('shop-page-open');
      document.documentElement.classList.remove('shop-page-open');
    };
  }, []);

  const order = useMemo(() => calculateOrder(sets, country), [sets, country]);

  const showGalleryPhoto = (index: number) => {
    const total = SHOP_GALLERY_IMAGES.length;
    setGalleryIndex(((index % total) + total) % total);
  };

  const showPreviousPhoto = () => showGalleryPhoto(galleryIndex - 1);
  const showNextPhoto = () => showGalleryPhoto(galleryIndex + 1);

  const handleCopyAmount = async () => {
    if (!order) return;
    const amount = order.grandTotal.toFixed(2);
    try {
      await navigator.clipboard.writeText(amount);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this amount for PayPal:', amount);
    }
  };

  return (
    <div className="shop-page">
      <div className="shop-toolbar">
        <button type="button" className="shop-back-button" onClick={onBack}>
          ← Back to game
        </button>
      </div>

      <div className="shop-scroll">
        <div className="shop-container">
          <div className="shop-content">
          <h1 className="shop-title">Migoyugo Store</h1>
          <div className="shop-lead">
            <p>Physical board, pieces, and mat — shipped from Canada via Canada Post.</p>
            <p>
              Each total includes everything: for Canada, GST and shipping; for the United
              States, shipping, duties, and processing.
            </p>
            <p className="shop-lead-cad">All prices in CAD.</p>
          </div>

          <div className="shop-layout">
            <div className="shop-product">
              <div className="shop-gallery">
                <div className="shop-gallery-frame">
                  <button
                    type="button"
                    className="shop-gallery-nav shop-gallery-nav-prev"
                    onClick={showPreviousPhoto}
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  {SHOP_GALLERY_IMAGES.map((image, index) => (
                    <img
                      key={image.src}
                      src={image.src}
                      alt={image.alt}
                      className={`shop-product-image shop-gallery-image${
                        index === galleryIndex ? ' is-active' : ''
                      }`}
                    />
                  ))}
                  <button
                    type="button"
                    className="shop-gallery-nav shop-gallery-nav-next"
                    onClick={showNextPhoto}
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                </div>
                <div className="shop-gallery-controls">
                  <div className="shop-gallery-dots" role="tablist" aria-label="Product photos">
                    {SHOP_GALLERY_IMAGES.map((image, index) => (
                      <button
                        key={image.src}
                        type="button"
                        role="tab"
                        aria-selected={index === galleryIndex}
                        aria-label={`Show photo ${index + 1} of ${SHOP_GALLERY_IMAGES.length}`}
                        className={`shop-gallery-dot${index === galleryIndex ? ' is-active' : ''}`}
                        onClick={() => showGalleryPhoto(index)}
                      />
                    ))}
                  </div>
                  <span className="shop-gallery-count" aria-live="polite">
                    {galleryIndex + 1} / {SHOP_GALLERY_IMAGES.length}
                  </span>
                </div>
              </div>
              <h2>What&apos;s included in each set</h2>
              <ul>
                <li>8×8 Migoyugo game mat</li>
                <li>80 playing pieces (40 white, 40 black)</li>
                <li>2 drawstring bags for the pieces</li>
                <li>1 blue carrying case</li>
              </ul>
            </div>

            <div className="shop-checkout">
              <h2>Select your order</h2>

              <label className="shop-field">
                <span>Number of sets</span>
                <select
                  value={sets}
                  onChange={(e) => setSets(Number(e.target.value) as 1 | 2)}
                >
                  <option value={1}>1 set</option>
                  <option value={2}>2 sets</option>
                </select>
              </label>

              <label className="shop-field">
                <span>Ship to</span>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value as ShipCountry)}
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
              </label>

              {order && (
                <div className="shop-breakdown">
                  <div className="shop-breakdown-row">
                    <span>{getOrderSummaryLabel(order.sets, order.country)}</span>
                    <span>{formatCad(order.grandTotal)}</span>
                  </div>
                  <div className="shop-breakdown-total">
                    <span>Total due at PayPal</span>
                    <span>{formatCad(order.grandTotal)}</span>
                  </div>
                </div>
              )}

              <div className="shop-amount-callout">
                <p className="shop-amount-label">Enter this exact amount at PayPal checkout:</p>
                <div className="shop-amount-row">
                  <strong className="shop-amount-value">
                    {order ? order.grandTotal.toFixed(2) : '—'} CAD
                  </strong>
                  <button type="button" className="shop-copy-btn" onClick={handleCopyAmount}>
                    {copied ? 'Copied!' : 'Copy amount'}
                  </button>
                </div>
              </div>

              <ol className="shop-steps">
                <li>Confirm the total above matches your order.</li>
                <li>Click PayPal below and enter <strong>{order?.grandTotal.toFixed(2)}</strong> CAD when prompted.</li>
                <li>Provide your name and full mailing address in the PayPal note.</li>
              </ol>

              <PayPalHostedButton />

              <p className="shop-note">
                Need 3 or more sets? Email{' '}
                <a href="mailto:founder@migoyugo.com">founder@migoyugo.com</a> for a quote.
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
